import type { Graphics } from 'pixi.js';
import type { Vec2 } from '../../sim/state';
import { OVERFLOW } from '../ground-layout';
import { POLE, type Way, at, trailAt } from '../../data/full/path-layout';
import { PATH_COLORS } from './path-way';

const PLANK = { half: 86, length: 26, gap: 4 };
const FLAG = { every: 30, half: 8, depth: 15, sag: 26 };
const BULB_EVERY = 4;

export function drawPlanks(g: Graphics, way: Way): void {
  const { half } = PLANK;
  let index = 0;
  for (let along = -OVERFLOW; along < way.length + OVERFLOW; along += PLANK.length + PLANK.gap) {
    const end = along + PLANK.length;
    const corners = [
      at(way, along, trailAt(way, along) - half),
      at(way, end, trailAt(way, end) - half),
      at(way, end, trailAt(way, end) + half),
      at(way, along, trailAt(way, along) + half),
    ];
    g.poly(corners.flatMap(({ x, y }) => [x, y]))
      .fill(index % 2 === 0 ? PATH_COLORS.plank : PATH_COLORS.plankDark)
      .stroke({ width: 2, color: PATH_COLORS.plankEdge });
    index += 1;
  }
}

export function drawBunting(g: Graphics, way: Way, lights: readonly number[]): Vec2[] {
  const near: Vec2[] = [];
  const far: Vec2[] = [];
  for (let along = POLE.first; along < way.length; along += POLE.gap) {
    near.push(at(way, along, trailAt(way, along) - POLE.out));
    far.push(at(way, along, trailAt(way, along) + POLE.out));
  }
  const bulbs: Vec2[] = [];
  const inward = way.down ? { x: 1, y: 0 } : { x: 0, y: 1 };
  near.forEach((from, index) => {
    const nextNear = near[index + 1];
    const nextFar = far[index + 1];
    const sameFar = far[index];
    if (nextNear !== undefined) {
      string(g, from, nextNear, scaled(inward, FLAG.sag), lights, index, bulbs);
    }
    if (nextFar !== undefined && sameFar !== undefined) {
      string(g, sameFar, nextFar, scaled(inward, -FLAG.sag), lights, index + 1, bulbs);
    }
    if (index % 4 === 1 && nextFar !== undefined) {
      string(g, from, nextFar, { x: 0, y: 0 }, lights, index, bulbs);
    }
    if (index % 4 === 3 && sameFar !== undefined) {
      string(g, from, sameFar, scaled(alongUnit(way), 40), lights, index, bulbs);
    }
  });
  for (const { x, y } of [...near, ...far]) {
    g.circle(x, y, POLE.radius)
      .fill(PATH_COLORS.pole)
      .stroke({ width: 3, color: PATH_COLORS.brass });
  }
  return bulbs;
}

const scaled = (v: Vec2, k: number): Vec2 => ({ x: v.x * k, y: v.y * k });
const alongUnit = (way: Way): Vec2 => (way.down ? { x: 0, y: 1 } : { x: 1, y: 0 });

function string(
  g: Graphics,
  a: Vec2,
  b: Vec2,
  sag: Vec2,
  lights: readonly number[],
  phase: number,
  bulbs: Vec2[],
): void {
  const control = { x: (a.x + b.x) / 2 + sag.x, y: (a.y + b.y) / 2 + sag.y };
  g.moveTo(a.x, a.y)
    .quadraticCurveTo(control.x, control.y, b.x, b.y)
    .stroke({ width: 1.6, color: PATH_COLORS.brass, alpha: 0.7 });
  const count = Math.max(2, Math.round(Math.hypot(b.x - a.x, b.y - a.y) / FLAG.every));
  const hang = Math.hypot(sag.x, sag.y) > 0 ? sag : { x: -(b.y - a.y), y: b.x - a.x };
  for (let i = 1; i < count; i++) {
    const t = i / count;
    const x = (1 - t) ** 2 * a.x + 2 * (1 - t) * t * control.x + t ** 2 * b.x;
    const y = (1 - t) ** 2 * a.y + 2 * (1 - t) * t * control.y + t ** 2 * b.y;
    const tx = 2 * (1 - t) * (control.x - a.x) + 2 * t * (b.x - control.x);
    const ty = 2 * (1 - t) * (control.y - a.y) + 2 * t * (b.y - control.y);
    const size = Math.hypot(tx, ty) || 1;
    const [ux, uy] = [tx / size, ty / size];
    const side = -uy * hang.x + ux * hang.y >= 0 ? 1 : -1;
    const [nx, ny] = [-uy * side, ux * side];
    const color = lights[(i + phase) % lights.length] ?? PATH_COLORS.brass;
    g.poly([
      x - ux * FLAG.half,
      y - uy * FLAG.half,
      x + ux * FLAG.half,
      y + uy * FLAG.half,
      x + nx * FLAG.depth,
      y + ny * FLAG.depth,
    ])
      .fill(color)
      .stroke({ width: 1.4, color: PATH_COLORS.outline });
    if (i % BULB_EVERY === 0) {
      g.circle(x, y, 3.5).fill(PATH_COLORS.bulb);
      bulbs.push({ x, y });
    }
  }
}
